// src/services/inventory/inventoryService.ts
// Magazzino: la merce entra con la scansione, esce perché usata in servizio
// o perché venduta al cliente. La giacenza la ricalcola il database (trigger
// su InventoryMovements), qui non si scrive mai `stock` a mano.
import { supabase } from '@/lib/supabase';

export type MovementType = 'in' | 'use' | 'sale';

export const PRODUCT_CATEGORIES = [
    'Colore',
    'Cura capelli',
    'Styling',
    'Rivendita',
    'Altro',
] as const;

export interface InventoryProduct {
    id: number;
    shop_id: number;
    name: string;
    category: string;
    barcode: string | null;
    price: number;
    min_threshold: number;
    stock: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface InventoryMovement {
    id: number;
    shop_id: number;
    product_id: number;
    type: MovementType;
    quantity: number;
    unit_price: number | null;
    total_amount: number | null;
    client_id: string | null;
    hairdresser_id: string | null;
    sale_id: number | null;
    note: string | null;
    created_by: string | null;
    created_at: string;
}

type Result<T> = { success: boolean; error?: string; data?: T };

export const fetchProducts = async (shopId: number | string): Promise<Result<InventoryProduct[]>> => {
    const { data, error } = await supabase
        .from('InventoryProducts')
        .select('*')
        .eq('shop_id', shopId)
        .eq('is_active', true)
        .order('name');

    if (error) return { success: false, error: error.message };
    return { success: true, data: (data || []) as InventoryProduct[] };
};

export const fetchMovements = async (shopId: number | string): Promise<Result<InventoryMovement[]>> => {
    const { data, error } = await supabase
        .from('InventoryMovements')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false })
        .limit(500);

    if (error) return { success: false, error: error.message };
    return { success: true, data: (data || []) as InventoryMovement[] };
};

export interface CreateProductInput {
    shop_id: number;
    name: string;
    category: string;
    barcode?: string | null;
    price: number;
    min_threshold?: number;
}

export const createProduct = async (input: CreateProductInput): Promise<Result<InventoryProduct>> => {
    const { data, error } = await supabase
        .from('InventoryProducts')
        .insert({
            shop_id: input.shop_id,
            name: input.name,
            category: input.category,
            barcode: input.barcode || null,
            price: input.price,
            min_threshold: input.min_threshold ?? 0,
        })
        .select()
        .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data as InventoryProduct };
};

export const updateProduct = async (
    id: number,
    patch: Partial<Pick<InventoryProduct, 'name' | 'category' | 'price' | 'min_threshold' | 'barcode'>>
): Promise<Result<InventoryProduct>> => {
    const { data, error } = await supabase
        .from('InventoryProducts')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data as InventoryProduct };
};

/** Un prodotto con movimenti non si elimina (vincolo sul database): si archivia. */
export const archiveProduct = async (id: number): Promise<Result<null>> => {
    const { error } = await supabase
        .from('InventoryProducts')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

    if (error) return { success: false, error: error.message };
    return { success: true };
};

export const deleteProduct = async (id: number): Promise<Result<null>> => {
    const { error } = await supabase.from('InventoryProducts').delete().eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
};

export interface MovementInput {
    shop_id: number;
    product_id: number;
    type: MovementType;
    quantity: number;
    unit_price?: number | null;
    client_id?: string | null;
    hairdresser_id?: string | null;
    sale_id?: number | null;
    note?: string | null;
    created_by?: string | null;
}

export const registerMovement = async (input: MovementInput): Promise<Result<InventoryMovement>> => {
    const unit = input.unit_price ?? null;
    const { data, error } = await supabase
        .from('InventoryMovements')
        .insert({
            shop_id: input.shop_id,
            product_id: input.product_id,
            type: input.type,
            quantity: input.quantity,
            unit_price: unit,
            total_amount: unit != null ? Number((unit * input.quantity).toFixed(2)) : null,
            client_id: input.client_id ?? null,
            hairdresser_id: input.hairdresser_id ?? null,
            sale_id: input.sale_id ?? null,
            note: input.note ?? null,
            created_by: input.created_by ?? null,
        })
        .select()
        .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data as InventoryMovement };
};

export interface SaleLine {
    product_id: number;
    quantity: number;
    unit_price: number;
}

export interface RegisterSaleInput {
    shop_id: number;
    client_id?: string | null;
    hairdresser_id?: string | null;
    created_by?: string | null;
    note?: string | null;
    lines: SaleLine[];
}

/**
 * Vendita al banco: una testata (chi ha comprato, chi ha venduto) e una riga
 * di movimento per prodotto, così la giacenza scende da sola.
 */
export const registerSale = async (
    input: RegisterSaleInput
): Promise<Result<{ sale_id: number; movements: InventoryMovement[] }>> => {
    const total = input.lines.reduce((sum, l) => sum + l.unit_price * l.quantity, 0);

    const { data: sale, error: saleError } = await supabase
        .from('InventorySales')
        .insert({
            shop_id: input.shop_id,
            client_id: input.client_id ?? null,
            hairdresser_id: input.hairdresser_id ?? null,
            total_amount: Number(total.toFixed(2)),
            note: input.note ?? null,
            created_by: input.created_by ?? null,
        })
        .select()
        .single();

    if (saleError || !sale) return { success: false, error: saleError?.message };

    const rows = input.lines.map((l) => ({
        shop_id: input.shop_id,
        product_id: l.product_id,
        type: 'sale' as MovementType,
        quantity: l.quantity,
        unit_price: l.unit_price,
        total_amount: Number((l.unit_price * l.quantity).toFixed(2)),
        client_id: input.client_id ?? null,
        hairdresser_id: input.hairdresser_id ?? null,
        sale_id: sale.id,
        created_by: input.created_by ?? null,
    }));

    const { data: movements, error: movError } = await supabase
        .from('InventoryMovements')
        .insert(rows)
        .select();

    if (movError) {
        // testata senza righe = vendita fantasma nei report: la togliamo
        await supabase.from('InventorySales').delete().eq('id', sale.id);
        return { success: false, error: movError.message };
    }

    return {
        success: true,
        data: { sale_id: sale.id, movements: (movements || []) as InventoryMovement[] },
    };
};
